// An organisation's tax categories (F-PAY-11; spec/06 §10.1).
//
//   GET   /v1/organizations/{o}/tax-categories                 the organisation's categories   settings:read
//   POST  /v1/organizations/{o}/tax-categories                 define one                      tax:manage
//   PATCH /v1/organizations/{o}/tax-categories/{id}            edit or archive one             tax:manage
//
// A **tax category** is the organisation's data, chosen by its accountant (spec/06 §10.1): a name, the HSN
// (goods) or SAC (services) code printed on a tax invoice, and a GST rate in basis points. Products, passes
// and the venue's custom Sell lines carry a category whatever their venue's tax mode, so a venue that
// registers later needs no change to its catalogue; the engine charges the rate only at a `regular` venue
// (spec/06 §10.3). India defaults are seeded the first time the organisation's categories are read, and
// are editable. A category is never deleted — a sale line keeps the code and rate it was sold under — it is
// **archived**, which takes it off the pickers.
//
// A rate change applies to what is sold from then on: every product in the category takes the new rate, and
// no placed sale is re-priced (spec/06 §10.3 rule 3). Every write is audited.
//
// `cessRateBps` is recorded for the accountant; no cess is charged yet (no supply a venue makes today carries
// one). `taxPoint` is `sale` for everything but a wallet top-up, which is taxed where it is spent (`redemption`,
// spec/06 §10.3 rule 6).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { TaxCategoryIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { BasisPointsSchema, IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { SETTINGS_READ, TAX_MANAGE } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
/** An HSN (goods, 4–8 digits) or SAC (services, 6 digits beginning 99) code, as printed on a tax invoice. */
export const HSN_SAC_PATTERN = /^[0-9]{4,8}$/;
export const HsnSacSchema = z
    .string()
    .trim()
    .regex(HSN_SAC_PATTERN, 'Expected an HSN or SAC code: 4 to 8 digits.')
    .openapi('HsnSac', { description: 'An HSN (goods) or SAC (services) code, 4 to 8 digits (spec/06 §10.1).', example: '996331' });
/** When a category's tax is charged (spec/06 §10.1): at the sale, or where wallet money is spent. */
export const TaxPointSchema = z.enum(['sale', 'redemption']).openapi('TaxPoint', {
    description: 'When the tax is charged: at the sale, or at redemption (a wallet top-up, taxed where it is spent).',
});
const NAME = z.string().trim().min(1).max(80);
export const TaxCategorySchema = z
    .object({
    id: TaxCategoryIdSchema,
    name: NAME,
    /** The HSN or SAC code printed on a tax invoice's line; null when the accountant has not set one. */
    hsnSac: z.string().nullable(),
    /** The GST rate, in basis points (1800 = 18%). Charged only at a `regular` venue. */
    rateBps: BasisPointsSchema,
    /** A compensation cess rate, recorded for the accountant; not charged yet. Null for none. */
    cessRateBps: BasisPointsSchema.nullable(),
    taxPoint: TaxPointSchema,
    /** An archived category is off the pickers; what was sold under it keeps its code and rate. */
    archived: z.boolean(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('TaxCategory', { description: 'An organisation’s tax category: name, HSN/SAC code and GST rate (spec/06 §10.1).' });
export const TaxCategoryListSchema = z
    .object({ categories: z.array(TaxCategorySchema) })
    .openapi('TaxCategoryList');
export const CreateTaxCategorySchema = z
    .object({
    name: NAME,
    hsnSac: HsnSacSchema.optional(),
    rateBps: BasisPointsSchema,
    cessRateBps: BasisPointsSchema.optional(),
    taxPoint: TaxPointSchema.optional(),
})
    .strict()
    .openapi('CreateTaxCategoryRequest');
export const UpdateTaxCategorySchema = z
    .object({
    name: NAME.optional(),
    /** A new code, or null to clear it. */
    hsnSac: HsnSacSchema.nullable().optional(),
    rateBps: BasisPointsSchema.optional(),
    cessRateBps: BasisPointsSchema.nullable().optional(),
    taxPoint: TaxPointSchema.optional(),
    archived: z.boolean().optional(),
})
    .strict()
    .refine((body) => Object.keys(body).length > 0, 'Change at least one field.')
    .openapi('UpdateTaxCategoryRequest');
const TaxCategoryParamsSchema = OrganizationScopeParamsSchema.extend({ taxCategoryId: TaxCategoryIdSchema }).openapi('TaxCategoryParams');
export const listTaxCategoriesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/tax-categories',
    operationId: 'listTaxCategories',
    tags: ['tax'],
    summary: 'List the organisation’s tax categories',
    description: 'Every tax category of the organisation, archived ones last (spec/06 §10.1). The India defaults — Gaming time, Food and ' +
        'beverages, Packaged goods, Memberships, Wallet top-up — are seeded the first time they are read. Requires `settings:read`.',
    [PERMISSION_EXTENSION]: SETTINGS_READ,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(TaxCategoryListSchema, 'The organisation’s tax categories.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createTaxCategoryRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/tax-categories',
    operationId: 'createTaxCategory',
    tags: ['tax'],
    summary: 'Define a tax category',
    description: 'Defines a tax category: a name, an HSN or SAC code and a GST rate (spec/06 §10.1). Audited. Requires `tax:manage`.',
    [PERMISSION_EXTENSION]: TAX_MANAGE,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreateTaxCategorySchema } }, required: true },
    },
    responses: {
        201: jsonResponse(TaxCategorySchema, 'The category, defined.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        409: problemResponse('A category with that name already exists in this organisation.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateTaxCategoryRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/tax-categories/{taxCategoryId}',
    operationId: 'updateTaxCategory',
    tags: ['tax'],
    summary: 'Edit or archive a tax category',
    description: 'Changes a category’s name, code or rate, or archives it (spec/06 §10.1). A new rate applies to what is sold from then on — ' +
        'every product in the category takes it — and no placed sale is re-priced. Audited. Requires `tax:manage`.',
    [PERMISSION_EXTENSION]: TAX_MANAGE,
    request: {
        params: TaxCategoryParamsSchema,
        body: { content: { 'application/json': { schema: UpdateTaxCategorySchema } }, required: true },
    },
    responses: {
        200: jsonResponse(TaxCategorySchema, 'The category, changed.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such tax category in this organisation.', 'not-found'),
        409: problemResponse('A category with that name already exists in this organisation.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=tax-categories.js.map